import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentService } from 'app/document/services/document.service';
import { GroupDataService } from 'app/services/group-data.service';
import { OfflineService } from 'app/services/offline.service';
import { PlanGroup, PlanMetadata, PlanUser } from 'fitforce-document-sync';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

export interface CopyPlanDialogComponentData {
  plan: PlanMetadata;
  userUUID: string;
}

@Component({
  selector: 'copy-plan-dialog',
  templateUrl: './copy-plan-dialog.component.html',
  styleUrls: ['./copy-plan-dialog.component.scss']
})
export class CopyPlanDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  displayedGroups: PlanGroup[] = [];
  displayedUsers: PlanUser[] = [];
  documentServiceInitSubscription: Subscription = new Subscription();
  saving: boolean;
  isOnline: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CopyPlanDialogComponentData,
    private formBuilder: FormBuilder,
    public documentService: DocumentService,
    private groupDataService: GroupDataService,
    public offline: OfflineService,
    private dialog: MatDialogRef<CopyPlanDialogComponent>
  ) {}

  ngOnInit() {
    this.isOnline = this.offline.isOnline();
    if (this.data.plan !== undefined) {
      this.saving = false;
      this.documentServiceInitSubscription = this.documentService.init().subscribe();

      this.form = this.formBuilder.group({
        name: this.formBuilder.control(`Copy of ${this.data.plan.name}`, [Validators.required]),
        startDate: this.formBuilder.control(this.data.plan.startDate.toDate(), [Validators.required]),
        numWeeks: this.formBuilder.control(this.data.plan.numWeeks, [Validators.required, Validators.min(1)]),
        notes: this.formBuilder.control(this.data.plan.notes),
        publishedToGroups: new FormArray([]),
        sharedWithUsers: new FormArray([])

      });

      /*
        Note:
          Only works online!
          This implementation only display the intersection between groups the current user is an owner of and groups the plan
          has already been published too.
       */
      if (this.isOnline) {
        // Look through the list of groups and find those that are owned by current user
        this.groupDataService.groups.subscribe((allGroups) => {
          const ownedGroupIDs: string[] = [];
          allGroups.forEach(group => {
            group.owners.forEach(owner => {
              if (owner.uuid === this.data.userUUID) {
                ownedGroupIDs.push(group.uuid);
              }
            });
          });

          // display groups that the current group has been published too && are owned by current user
          this.displayedGroups = this.data.plan.publishedToGroups.filter(group => {
            return ownedGroupIDs.includes(group.uuid);
          });

          this.data.plan.sharedWithUsers
          .filter(user => user.uuid !== this.documentService.store.currentUser.uuid)
          .forEach(user => {
            this.displayedUsers.push(user);
          });

          // generate boxes after we have displayedGroups and displayedUsers
          this.generateBoxes();


        });
      }
    } else {
      console.error('CopyPlanDialogComponent: Passed undefined plan in data object.');
    }
  }

  ngOnDestroy(): void {
    this.documentServiceInitSubscription.unsubscribe();
  }

  get controls() {
    return {
      name: this.form.get('name'),
      startDate: this.form.get('startDate'),
      numWeeks: this.form.get('numWeeks'),
      notes: this.form.get('notes'),
      publishedToGroups: this.form.get('publishedToGroups'),
      sharedWithUsers: this.form.get('sharedWithUsers'),
    };
  }

  /**
   * Creates form control for each box
   */
  private generateBoxes() {
    this.displayedGroups.forEach((o, i) => {
      const control = new FormControl(true); // if first item set to true, else false
      (this.form.controls.publishedToGroups as FormArray).push(control);
    });

    this.displayedUsers.forEach((o, i) => {
      const control = new FormControl(true); // if first item set to true, else false
      (this.form.controls.sharedWithUsers as FormArray).push(control);
    });
  }

  /**
   * When dialog close, return items that have changed
   */
  save() {
    const users = [];
    // this.controls.sharedWithUsers.value.forEach((userChecked, i) => {
    //   if (userChecked) {
    //     users.push(this.displayedUsers[i]);
    //   }
    // });

    const groups = [];
    // this.controls.publishedToGroups.value.forEach((userChecked, i) => {
    //   if (userChecked) {
    //     groups.push(this.displayedGroups[i]);
    //   }
    // });

    const override: any = {
      sharedWithUsers: users,
      publishedToGroups: groups,
    };

    // only append the values that changed
    if (this.controls.name.value !== `Copy of ${this.data.plan.name}`) {
      override.name = this.controls.name.value;
    }

    if (!moment(this.controls.startDate.value).isSame(this.data.plan.startDate)) {
      override.startDate = moment(this.controls.startDate.value);
    }

    if (this.controls.numWeeks.value !== this.data.plan.numWeeks) {
      override.numWeeks = this.controls.numWeeks.value;
    }

    if (this.controls.notes.value !== this.data.plan.notes) {
      override.notes = this.controls.notes.value;
    }
    this.form.disable();
    this.saving = true;
    this.documentService.copyPlan(this.data.plan, this.data.userUUID, override).subscribe(() => {
      this.saving = false;
      this.dialog.close();
    });
  }
}
